/**
 * informePdfApi — el PDF del informe de liquidación.
 *
 *   POST /api/pdf/generate
 *   { type: 'liquidacion_dia'|'liquidacion_mes', fecha|period, compania, action }
 *
 * EL PDF LO GENERA PLAYWRIGHT, no el diálogo de impresión del navegador. Es lo que le da
 * márgenes fijos, «Página X de Y», registro en `informes_accesos`, y la posibilidad de
 * subirlo a storage o mandarlo por correo — nada de lo cual `window.print()` puede dar.
 *
 * `compania` es OBLIGATORIA: el documento va dirigido a UNA aseguradora.
 *
 * @see api/pdf/generate.js — el endpoint
 * @see api/pdf/_informeLiquidacion.js — cómo se arma el documento
 */

const URL_PDF = '/api/pdf/generate';

/** El cuerpo común de las tres acciones. */
function cuerpo({ tipo, fecha, period, compania, action, emailTo }) {
  return {
    type: tipo === 'dia' ? 'liquidacion_dia' : 'liquidacion_mes',
    ...(tipo === 'dia' ? { fecha } : { period }),
    compania,
    action,
    ...(emailTo ? { emailTo } : {}),
  };
}

async function pedir(token, body, aceptaPdf) {
  const res = await fetch(URL_PDF, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // El error viene en JSON aunque la petición esperara un PDF.
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.error || `El generador de PDF respondió ${res.status}`);
  }
  return aceptaPdf ? res.blob() : res.json();
}

/**
 * Descarga el PDF y dispara el guardado en el navegador.
 *
 * El nombre lo decide el servidor por `Content-Disposition`, pero el atributo `download`
 * del ancla necesita uno igual: se usa el número del informe, que es el mismo que el
 * servidor arma.
 */
export async function descargarInformePdf(token, params, nombreSugerido) {
  const blob = await pedir(token, cuerpo({ ...params, action: 'download' }), true);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreSugerido || 'informe-liquidacion.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Sin esto el blob queda retenido hasta que se cierre la pestaña.
    URL.revokeObjectURL(url);
  }
}

/** Sube el PDF al bucket `settlement` y devuelve su fila de `archivos_storage`. */
export async function guardarInformePdf(token, params) {
  return pedir(token, cuerpo({ ...params, action: 'upload' }), false);
}

/**
 * Manda el PDF a la compañía.
 *
 * Sin `emailTo`, el servidor usa el correo de aviso del mandato de recaudación
 * (`datos_bancarios.email`) — el mismo que el documento imprime.
 */
export async function enviarInformePdf(token, params, emailTo) {
  return pedir(token, cuerpo({ ...params, action: 'upload_and_email', emailTo }), false);
}
